import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Paperclip, Upload, Download, Trash2, FileText, FileImage, FileVideo, FileArchive } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const MAX_BYTES = 25 * 1024 * 1024;

/**
 * <AttachmentsTab scopeType scopeId> — reusable upload + list + delete.
 * Supported scope_types: client, project, employee.
 * Storage backend: Emergent Object Storage (S3-compatible). Files are
 * uploaded via multipart/form-data and downloaded as authenticated blobs.
 */
export default function AttachmentsTab({ scopeType, scopeId, testid = "attachments" }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/attachments/${scopeType}/${scopeId}`);
      setRows(data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (scopeId) load(); }, [scopeType, scopeId]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let okCount = 0;
    try {
      for (const f of Array.from(files)) {
        if (f.size > MAX_BYTES) { toast.error(`${f.name} is over 25 MB`); continue; }
        try {
          const body = new FormData();
          body.append("file", f);
          await api.post(`/attachments/${scopeType}/${scopeId}`, body, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          okCount += 1;
        } catch (e) { toast.error(apiErrorMessage(e, `Could not upload ${f.name}`)); }
      }
      await load();
      if (okCount > 0) toast.success(`${okCount} file${okCount === 1 ? "" : "s"} uploaded`);
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (a) => {
    try {
      const resp = await api.get(`/attachments/file/${a.id}/download`, { responseType: "blob" });
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: a.content_type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(apiErrorMessage(e, "Download failed")); }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/attachments/${a.id}`);
      toast.success("Attachment deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const total = rows.reduce((s, a) => s + (a.size || 0), 0);

  return (
    <section className="space-y-4" data-testid={testid}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={`border-2 border-dashed p-6 text-center transition ${dragOver ? "border-primary bg-primary/5" : "border-border"}`}
        data-testid={`${testid}-dropzone`}
      >
        <Paperclip className="w-6 h-6 mx-auto text-muted-foreground" />
        <div className="mt-2 text-sm font-medium">Drop files here</div>
        <div className="text-xs text-muted-foreground">PDF, images, ZIP — up to 25 MB each</div>
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" className="rounded-none h-8 text-xs mt-3" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload-btn`}>
          <Upload className="w-3 h-3 mr-1" /> {uploading ? "Uploading…" : "Choose files"}
        </Button>
      </div>

      {busy && rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No attachments yet"
          description="Drop a file above or click Choose files to upload."
          icon={Paperclip}
          testid={`${testid}-empty`}
        />
      ) : (
        <>
          <div className="text-xs text-muted-foreground">{rows.length} file{rows.length === 1 ? "" : "s"} · {fmtBytes(total)}</div>
          <div className="border border-border divide-y divide-border" data-testid={`${testid}-list`}>
            {rows.map((a) => (
              <div key={a.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`${testid}-row-${a.id}`}>
                <div className="col-span-6 flex items-center gap-2 min-w-0">
                  <FileIcon contentType={a.content_type} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.filename}</div>
                    <div className="text-[11px] text-muted-foreground">{a.content_type || "file"} · {fmtBytes(a.size)}</div>
                  </div>
                </div>
                <div className="col-span-3 text-xs text-muted-foreground truncate">{a.uploader_name}</div>
                <div className="col-span-2 text-xs text-muted-foreground">{fmtRelative(a.created_at)}</div>
                <div className="col-span-1 flex justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => download(a)} title="Download" data-testid={`${testid}-download-${a.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  {a.uploader_id === user?.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`${testid}-delete-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete attachment?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {a.filename} will be removed permanently. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(a)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function FileIcon({ contentType }) {
  const ct = (contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return <FileImage className="w-4 h-4 text-muted-foreground shrink-0" />;
  if (ct.startsWith("video/")) return <FileVideo className="w-4 h-4 text-muted-foreground shrink-0" />;
  if (ct.includes("zip") || ct.includes("compressed")) return <FileArchive className="w-4 h-4 text-muted-foreground shrink-0" />;
  return <FileText className="w-4 h-4 text-muted-foreground shrink-0" />;
}
